import { router, useForm } from "@inertiajs/react";
import { Modal, ToggleSwitch } from "@switchdreams/ui";
import axios from "axios";
import jsPDF from "jspdf";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { BiPlusCircle } from "react-icons/bi";
import { BsFileEarmarkPdf } from "react-icons/bs";
import { FiDownload, FiSave, FiSend } from "react-icons/fi";
import { ReactSortable } from "react-sortablejs";

import ButtonIcon from "@/components/Buttons/SimpleButton";
import Layout from "@/components/Layout";
import PercentageLetterModal from "@/components/Modal/PercentageLetterModal";
import ProgressBarComponent from "@/components/ProgressBar";
import TagSelector, { TagSelectorOption } from "@/components/TagSelector";
import Typography from "@/components/Typography";
import { PageContainer } from "@/constants/listIndexStyle";
import { AlertContext } from "@/contexts/Alert";

import { OrderItem } from "./types";

const FacilityCard = ({ action, consumer, data, setData, setPercentages }) => {
  const [consumerSum, setConsumerSum] = useState(
    action == "show"
      ? consumer.facilities.reduce((acc: any, current: any) => acc + current.facility_percentage, 0)
      : data.facility_percentages_attributes
          .filter((percentage: any) =>
            consumer.facilities
              .map((facility: any) => facility.facility_id)
              .includes(percentage.facility_id),
          )
          .reduce((acc: any, current: any) => acc + current.percentage, 0) / 100,
  );

  useEffect(() => {
    setConsumerSum(
      data.facility_percentages_attributes
        .filter((percentage: any) =>
          consumer.facilities
            .map((facility: any) => facility.facility_id)
            .includes(percentage.facility_id),
        )
        .reduce((acc: any, current: any) => acc + current.percentage, 0) / 100,
    );
  }, [consumer.facilities, data]);

  return (
    <div
      className={`flex h-fit w-full flex-col justify-center rounded-3xl ${
        consumerSum > 0 || action == "create" ? "bg-white" : "bg-grey200"
      } md:w-[49%]`}
    >
      <div
        className={`flex w-full flex-row items-center gap-4 ${
          consumer.facilities.length > 0 && "border-b"
        } border-b-gray-100 p-4`}
      >
        <div>
          <Typography
            variant="pLarge"
            color="grey600"
            className="rounded border border-gray-200 p-1 font-semibold"
          >
            {consumer.consumer_id}
          </Typography>
        </div>
        <div className="flex flex-col">
          <Typography variant="h6" color="grey800">
            {consumer.consumer_name}
          </Typography>
          <Typography variant="pLarge" color="grey500" className="pt-0">
            Usando: {Number(consumerSum.toFixed(2))}%
          </Typography>
        </div>
      </div>
      {consumer.facilities.map((facility: any, index: number) => {
        const handlePercentage = () => {
          const inputs: any = Array.prototype.slice.call(
            document.getElementsByClassName(`inputPercentage-${consumer.consumer_id}`),
          );
          setPercentages(
            inputs.map((element: any) => parseFloat(element.value.replace(/,/g, "."))),
          );
          const element: any = document.getElementById(`input-${facility.facility_code}`);
          const percentage_float = parseFloat(element.value.replace(/,/g, ".")) * 100;
          const new_facility_percentages_attributes: any = [
            ...data.facility_percentages_attributes.filter(
              (facility_percentage: any) => facility_percentage.facility_id != facility.facility_id,
            ),
            { facility_id: facility.facility_id, percentage: percentage_float },
          ];
          setData("facility_percentages_attributes", new_facility_percentages_attributes);
        };

        return (
          <div className="flex w-full flex-row items-center justify-between gap-4 p-4" key={index}>
            <Typography
              variant="pLarge"
              color="grey800"
              className="rounded border border-gray-100 p-1 font-semibold"
            >
              {facility.facility_code}
            </Typography>
            <Typography
              variant="pLarge"
              color="grey900"
              className="hidden w-full text-left sm:block"
            >
              {facility.facility_address}
            </Typography>
            <div className="flex items-center gap-2">
              <input
                id={`input-${facility.facility_code}`}
                type="number"
                className={`inputPercentage-${consumer.consumer_id} w-20 rounded-2xl border-2 p-2`}
                defaultValue={action == "show" ? facility.facility_percentage : 0}
                data-mask="99,9"
                data-mask-selectonfocus="true"
                onBlur={() => handlePercentage()}
              />
              <p>%</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PercentageMode = ({ letter, data, setData, action, setPercentages }) => {
  return (
    <div
      className="flex h-fit w-full flex-row flex-wrap justify-between gap-4 rounded-3xl"
      id="consumers-container"
    >
      {letter.consumers_with_facilities.map((consumer: any, idx: number) => (
        <FacilityCard
          key={idx}
          consumer={consumer}
          data={data}
          setData={setData}
          action={action}
          setPercentages={setPercentages}
        />
      ))}
    </div>
  );
};

const OrderFacilityCard = ({
  index,
  consumer_name,
  facility_code,
  id,
  data,
  setData,
  onClick,
}: {
  index?: number;
  consumer_name: string;
  facility_code: string;
  id: number;
  data?: any;
  setData?: any;
  onClick?: () => void;
}) => {
  return (
    <div
      key={index}
      className={`flex h-fit w-full flex-col justify-center rounded-3xl ${
        index ? "bg-white" : "cursor-pointer bg-coolGray-300"
      } md:w-full`}
      onClick={onClick}
    >
      <div
        className={`flex w-full flex-row items-center justify-between gap-4 border-b-gray-200 p-4`}
      >
        <Typography variant="pLarge" color="grey800" className="p-1">
          {index && (
            <>
              <span className="font-bold text-grey950">{index}</span> -{" "}
            </>
          )}
          Consumidor: {consumer_name} - Facility: {facility_code}
        </Typography>
        {index && (
          <input
            type="checkbox"
            checked={data.leftover === id}
            onChange={() => setData({ ...data, leftover: id })}
            className="size-4 rounded-3xl"
          />
        )}
      </div>
    </div>
  );
};

const PercentageLetter = ({ letter, action = "show", back_power_plant_id }: any) => {
  const { showAlert } = useContext<any>(AlertContext);
  const [open, setOpen] = useState(false);
  const [totalSum, setTotalSum] = useState(0);
  const [percentages, setPercentages] = useState([]);
  const [barColor, setBarColor] = useState("");

  const initialFacilityPercentage = letter.consumers_with_facilities.flatMap((consumer) => {
    return consumer.facilities.map((facility) => ({
      facility_id: facility.facility_id,
      percentage: action === "create" ? 0 : facility.facility_percentage * 100,
    }));
  });

  const { data, setData, post } = useForm<{
    start_date: string;
    power_plant_ids: number[];
    return_to_power_plant_id: number | null;
    facility_percentages_attributes: any[];
    facility_orders_attributes: OrderItem[];
    letter_type: "preference" | "percentage";
    leftover: number | null;
  }>({
    start_date: letter.start_date ? String(letter.start_date).substring(0, 10) : "",
    power_plant_ids: letter.power_plant_ids ?? [],
    return_to_power_plant_id: back_power_plant_id ?? null,
    facility_percentages_attributes: initialFacilityPercentage,
    facility_orders_attributes: letter.current_order ?? [],
    letter_type: letter.letter_type ?? "percentage",
    leftover: letter.leftover ?? null,
  });
  const date = new Date(letter.start_date);
  const dateFormated = `${date.getFullYear()}-${
    date.getMonth() + 1 >= 10 ? date.getMonth() + 1 : "0" + Number(date.getMonth() + 1)
  }-${date.getDate() + 1}`;
  const [currentOrder, setCurrentOrder] = useState<OrderItem[]>(letter.current_order ?? []);
  const initialLetterData = useRef({
    action,
    consumersWithFacilities: letter.consumers_with_facilities,
    data,
    dateFormated,
  });
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const powerPlantOptions: TagSelectorOption[] = letter.power_plant_options ?? [];
  const selectedPowerPlantNames = powerPlantOptions
    .filter((powerPlant) => data.power_plant_ids.includes(powerPlant.id))
    .map((powerPlant) => powerPlant.name)
    .join(", ");
  const isCreate = action === "create";
  const isPercentageMode = data.letter_type === "percentage";

  const handleBack = () => {
    const destination = back_power_plant_id
      ? `/manager/power-plants/${back_power_plant_id}/percentage-letters`
      : "/manager/power-plants";

    router.get(destination);
  };

  const toggleLetterType = () => {
    setData({
      ...data,
      letter_type: isPercentageMode ? "preference" : "percentage",
    });
  };

  const notAddedFacilities = useMemo(() => {
    const res: OrderItem[] = [];
    letter.consumers_with_facilities.forEach((consumer) => {
      consumer.facilities.forEach((facility) => {
        if (!currentOrder.find((order) => order.facility_code == facility.facility_code)) {
          res.push({
            consumer_name: consumer.consumer_name,
            facility_code: facility.facility_code,
            facility_id: facility.facility_id,
            id: facility.facility_id,
          });
        }
      });
    });

    return res;
  }, [letter.consumers_with_facilities, currentOrder]);

  const handleBarColor = (sum: number) => {
    if (sum < 100) {
      return "#479657";
    } else if (sum == 100) {
      return "#A2D729";
    } else {
      return "#E95D49";
    }
  };

  useEffect(() => {
    const initialData = initialLetterData.current;
    if (initialData.action == "show") {
      setPercentages(
        initialData.consumersWithFacilities.map((consumer) =>
          consumer.facilities.map((facility: any) => facility.facility_percentage),
        ),
      );

      const consumers: any = [];
      initialData.consumersWithFacilities.forEach((consumer: any) => {
        consumer.facilities.forEach((facility: any) => {
          consumers.push({
            facility_id: facility.facility_id,
            percentage: facility.facility_percentage * 100,
          });
        });
      });

      setData({
        ...initialData.data,
        facility_percentages_attributes: consumers,
        start_date: initialData.dateFormated,
      });
    }
    setBarColor("");
  }, [setData]);

  useEffect(() => {
    setData("facility_orders_attributes", currentOrder);
  }, [currentOrder, setData]);

  useEffect(() => {
    let sum = 0;
    sum = data.facility_percentages_attributes.reduce(
      (acc: any, item: any) => acc + item.percentage / 100,
      0,
    );
    setTotalSum(sum);
    setBarColor(handleBarColor(sum));
  }, [percentages, data.facility_percentages_attributes]);

  const updateLetter = () => {
    if (data.power_plant_ids.length === 0) {
      showAlert({ message: "Selecione pelo menos uma usina", type: "error" });
      return;
    }

    axios
      .patch(`/manager/percentage-letter/${letter.id}`, data)
      .then(() => {
        showAlert({
          message: "Carta de rateio atualizada",
          type: "success",
        });
        setOpen(false);
      })
      .catch((e) =>
        showAlert({
          message: `erro: ${e}`,
          type: "error",
        }),
      );
  };

  const createLetter = () => {
    if (data.power_plant_ids.length === 0) {
      showAlert({ message: "Selecione pelo menos uma usina", type: "error" });
      return;
    }

    post("/manager/percentage-letter");
  };

  const exportLetter = async () => {
    const doc = new jsPDF({ orientation: "p" });
    const page = document.getElementById("page-container") as HTMLElement;

    if (!page) return;

    const exportPage = page.cloneNode(true) as HTMLElement;
    const sourceInputs = page.querySelectorAll<HTMLInputElement>("input");
    const exportInputs = exportPage.querySelectorAll<HTMLInputElement>("input");

    sourceInputs.forEach((input, index) => {
      if (exportInputs[index]) exportInputs[index].value = input.value;
    });

    exportPage.style.width = "1513px";
    exportPage.querySelector<HTMLElement>("#letter-type-container")?.remove();
    exportPage.querySelector<HTMLElement>("#add-facility-button")?.remove();

    const exportHeader = exportPage.querySelector<HTMLElement>("#letter-export-header");
    const exportTitle = exportPage.querySelector<HTMLElement>("#letter-export-title span");
    const powerPlantSelector = exportPage.querySelector<HTMLElement>("#power-plant-selector");
    const exportDateContainer = exportPage.querySelector<HTMLElement>("#export-date-container");
    const consumers = exportPage.querySelector<HTMLElement>("#consumers-container");
    const progress = exportPage.querySelector<HTMLElement>("#progress-bar");
    const exportContentWidth = "85%";

    if (exportHeader) {
      exportHeader.style.width = exportContentWidth;
      exportHeader.style.margin = "auto";
      exportHeader.style.paddingBottom = "20px";
    }

    if (exportTitle) {
      exportTitle.style.fontSize = "32px";
      exportTitle.style.lineHeight = "40px";
    }

    if (powerPlantSelector) {
      const powerPlantNames = document.createElement("p");
      powerPlantNames.textContent = selectedPowerPlantNames || letter.power_plant_name;
      powerPlantNames.style.fontSize = "18px";
      powerPlantNames.style.lineHeight = "28px";
      powerPlantSelector.replaceWith(powerPlantNames);
    }

    if (exportDateContainer) {
      const paragraph = document.createElement("p");
      paragraph.textContent = `Carta válida a partir de ${dateFormated}`;
      exportDateContainer.appendChild(paragraph);
      exportDateContainer.style.textAlign = "center";
    }

    if (consumers) {
      consumers.style.flexDirection = "column";
      consumers.style.alignItems = "center";
      consumers.style.marginTop = "16px";
      consumers.style.width = exportContentWidth;
      consumers.style.marginLeft = "auto";
      consumers.style.marginRight = "auto";
      Array.from(consumers.children).forEach((card) => {
        (card as HTMLElement).style.width = "100%";
      });
    }

    if (progress) {
      progress.style.width = exportContentWidth;
      progress.style.margin = "auto";
    }

    await doc.html(exportPage, {
      autoPaging: "text",
      margin: 10,
      width: 190,
      windowWidth: 1513,
      html2canvas: {
        allowTaint: true,
        backgroundColor: "#ebf2f59e",
      },
    });

    doc.save(letter.power_plant_name.concat(" - ", String(letter.start_date).substring(0, 7)));
  };

  return (
    <Layout>
      <PageContainer>
        <PercentageLetterModal
          isOpen={open}
          setIsOpen={setOpen}
          powerPlantName={selectedPowerPlantNames || letter.power_plant_name}
          data={data}
          setData={setData}
          onClick={action === "show" ? updateLetter : createLetter}
          action={action}
        />
        <div className="mb-16 flex w-full items-center justify-between">
          <ButtonIcon onClick={handleBack} variant="back" label="Voltar" />
          {isCreate ? (
            <ButtonIcon
              onClick={() => setOpen(true)}
              variant="simple"
              icon={<FiSend className="text-lg" />}
              label="Enviar carta"
              title="Enviar carta"
              className="!w-fit whitespace-nowrap px-4"
            />
          ) : (
            <div id="action-buttons" className="flex flex-row items-center gap-2 sm:gap-3">
              <ButtonIcon
                onClick={() => setOpen(true)}
                variant="simple"
                icon={<FiSave className="text-lg" />}
                label="Salvar edição"
                title="Salvar edição"
                className="whitespace-nowrap px-4"
              />
              <ButtonIcon
                onClick={exportLetter}
                variant="simple"
                icon={<FiDownload className="text-lg" />}
                title="Exportar carta"
                className="flex !h-10 !w-10 !min-w-0 flex-none items-center justify-center rounded-full !p-0"
              />
              <ButtonIcon
                onClick={() => router.visit(`/manager/percentage-letter-pdf/${letter.id}`)}
                variant="simple"
                icon={<BsFileEarmarkPdf className="text-lg" />}
                title="PDF da Light"
                className="flex !h-10 !w-10 !min-w-0 flex-none items-center justify-center rounded-full !p-0"
              />
            </div>
          )}
        </div>
        <div className="p-4" id="page-container">
          <div className="mb-8 w-full rounded-3xl bg-white p-4 sm:p-6">
            <div
              className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
              id="letter-export-header"
            >
              <div className="flex flex-col justify-start">
                <div id="letter-export-title">
                  <Typography variant="h1">Carta de Rateio</Typography>
                </div>
                <div id="power-plant-selector">
                  <TagSelector
                    options={powerPlantOptions}
                    selectedIds={data.power_plant_ids}
                    onChange={(ids) => setData("power_plant_ids", ids)}
                    placeholder="Selecione pelo menos uma usina"
                  />
                </div>
              </div>
              <div id="export-date-container" className="flex-shrink-0" />
            </div>

            <div id="letter-type-container-parent">
              <div
                className="mt-4 flex flex-row justify-between border-t border-grey200 pt-4"
                id="letter-type-container"
              >
                <label className="flex cursor-pointer items-center gap-2">
                  <ToggleSwitch checked={!isPercentageMode} onChange={toggleLetterType} />
                  <Typography variant="pLarge">
                    {isPercentageMode ? "Por porcentagem" : "Por ordem de prioridade"}
                  </Typography>
                </label>

                {data.letter_type === "preference" && (
                  <Typography variant="pLarge">Receptor do montante remanescente:</Typography>
                )}
              </div>
            </div>

            {isPercentageMode && (
              <div
                className="mt-4 flex w-full flex-col items-center justify-center gap-2 border-t border-grey200 pt-4 lg:flex-row"
                id="progress-bar"
              >
                <Typography variant="pLarge">Total distribuído:</Typography>
                <ProgressBarComponent percentage={Math.min(totalSum, 100)} color={barColor} />
                <Typography variant="pLarge">{Number(totalSum.toFixed(2))} %</Typography>
              </div>
            )}
          </div>

          {isPercentageMode ? (
            <PercentageMode
              letter={letter}
              data={data}
              setData={setData}
              action={action}
              setPercentages={setPercentages}
            />
          ) : (
            <div className="flex flex-col items-center" id="add-facility-parent">
              <ReactSortable
                list={currentOrder}
                setList={setCurrentOrder}
                className="flex w-full flex-col flex-wrap gap-4"
                id="consumers-container"
              >
                {currentOrder.map((item, index: number) => (
                  <OrderFacilityCard
                    index={index + 1}
                    consumer_name={item.consumer_name}
                    facility_code={item.facility_code}
                    key={index}
                    id={item.id}
                    data={data}
                    setData={setData}
                  />
                ))}
              </ReactSortable>
              <div
                className="mt-4 flex w-full cursor-pointer flex-row items-center justify-between gap-4 rounded-3xl bg-white p-4"
                onClick={() => setIsOrderModalOpen(true)}
                id="add-facility-button"
              >
                <Typography
                  variant="pLarge"
                  color="grey800"
                  className="flex items-center gap-2 font-semibold"
                >
                  <BiPlusCircle />
                  Adicionar nova facility
                </Typography>
              </div>
            </div>
          )}
        </div>
        <Modal
          open={isOrderModalOpen && data.letter_type === "preference"}
          setOpen={setIsOrderModalOpen}
          onClickCancel={() => setIsOrderModalOpen(false)}
          onClickConfirm={() => setIsOrderModalOpen(false)}
        >
          <div className="flex max-h-[75vh] flex-col gap-4">
            <Typography variant="h3">Selecione as novas usinas</Typography>
            <div className="flex flex-col gap-4 overflow-auto">
              {notAddedFacilities.map((facility, index) => (
                <OrderFacilityCard
                  consumer_name={facility.consumer_name}
                  facility_code={facility.facility_code}
                  key={index}
                  id={facility.id}
                  onClick={() => setCurrentOrder((currentOrder) => [...currentOrder, facility])}
                />
              ))}
            </div>
          </div>
        </Modal>
      </PageContainer>
    </Layout>
  );
};

export default PercentageLetter;
